import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import { brand } from 'utils/designTokens';

/**
 * Reusable empty state component for reports.
 */
export default function EmptyState({ type = 'nodata', message, onRetry }) {
  let Icon = InboxRoundedIcon;
  let title = 'No Data Found';
  let desc = message || 'No report data available';
  let iconColor = brand.primary;

  if (type === 'nofilters') {
    Icon = SearchOffRoundedIcon;
    title = 'No Results For Filters';
    desc = 'Try adjusting your search queries or date ranges to find what you are looking for.';
    iconColor = 'text.secondary';
  } else if (type === 'error') {
    Icon = ErrorOutlineRoundedIcon;
    title = 'Failed to Load Data';
    desc = message || 'An error occurred while loading the report data. Please try again.';
    iconColor = 'error.main';
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 5,
        textAlign: 'center',
        minHeight: 300,
        bgcolor: '#fff',
        borderRadius: 3,
        border: `1px dashed ${brand.border || '#e0e0e0'}`,
        animation: 'fadeIn 0.4s ease-out'
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          bgcolor: type === 'error' ? 'error.lighter' : type === 'nofilters' ? 'grey.100' : 'primary.lighter',
          color: iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2.5
        }}
      >
        <Icon sx={{ fontSize: 32 }} />
      </Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mb: onRetry ? 3 : 0 }}>
        {desc}
      </Typography>
      {onRetry && (
        <Button 
          variant="contained" 
          onClick={onRetry} 
          sx={{ 
            borderRadius: 2, 
            textTransform: 'none', 
            fontWeight: 600,
            bgcolor: brand.primary,
            '&:hover': { bgcolor: brand.primaryDark }
          }}
        >
          Retry Request
        </Button>
      )}
    </Box>
  );
}

EmptyState.propTypes = {
  type: PropTypes.oneOf(['nodata', 'nofilters', 'error']),
  message: PropTypes.string,
  onRetry: PropTypes.func
};
